import type { ReflectionAnswers, ReflectionReply, ReflectionTone } from "./types";
import { REFLECTION_AREAS } from "./options";

/**
 * Build a warm, nonjudgmental, non-clinical reply to the visitor's optional reflection answers.
 *
 * Product rules:
 * - Purely educational support and practical reflection, not clinical advice.
 * - Never use clinical labels (no clinical terminology, labels, or pathologizing language).
 * - Never imply imagination is inherently unhealthy.
 * - Affirm imagination as a normal human capacity.
 * - "steady": in control / no interference / skipped -> affirm imagination.
 * - "gentle-note": some difficulty steering or a little interference -> normalize, suggest practical ideas (wind-down, notebook, daydream window).
 * - "support-note": often hard to stop or quite a bit of interference in any area -> warm acknowledgment, "this is common and nothing to be ashamed of", suggest talking with a qualified mental health professional, point to calm information section.
 * - Works safely when answers are empty or all "prefer not to say".
 */
export function buildReflectionReply(answers?: ReflectionAnswers | null): ReflectionReply {
  const control = answers?.control ?? null;
  const areaMap = answers?.areas ?? {};

  // Count area severity
  let quiteABitCount = 0;
  let aLittleCount = 0;

  for (const area of REFLECTION_AREAS) {
    const val = areaMap[area.id];
    if (val === "quite a bit") {
      quiteABitCount++;
    } else if (val === "a little") {
      aLittleCount++;
    }
  }

  // Determine tone
  let tone: ReflectionTone = "steady";
  if (control === "often hard to stop" || quiteABitCount > 0) {
    tone = "support-note";
  } else if (control === "sometimes hard to steer" || aLittleCount > 0) {
    tone = "gentle-note";
  }

  // Construct warm paragraphs
  if (tone === "support-note") {
    return {
      tone,
      showSupportSection: true,
      paragraphs: [
        "Thank you for sharing this honest reflection. Rich inner worlds and vivid daydreams are a completely normal part of human consciousness, but when they begin pulling hard against your sleep, relationships, or everyday responsibilities, it is understandable to feel stretched.",
        "Experiencing this tension is common and nothing to be ashamed of. Many imaginative people find that daydreaming intensifies during periods of high stress, loneliness, or fatigue, acting as an understandable emotional retreat.",
        "You do not need to suppress your imagination. Rather, exploring these patterns with a supportive, qualified counselor or mental-health professional can offer personalized tools to strengthen your real-world grounding while honoring your creative depth. We have also included some calm, supportive resources below if you would like to explore further.",
      ],
    };
  }

  if (tone === "gentle-note") {
    return {
      tone,
      showSupportSection: false,
      paragraphs: [
        "Your reflection shows a healthy awareness of how your inner world interacts with your waking life. Imagination is a valuable creative engine, and it is very common for thoughts to wander during routine tasks or when energy runs low.",
        "If you occasionally find it tricky to steer your attention back to the room, small practical structures can help. Many people benefit from keeping a 'holding notebook' beside their desk to jot down daydream concepts for later, establishing a gentle screen-free wind-down before bed, or designating a 15-minute 'imagination window' during a walk.",
        "The action plan in Section 4 is designed around bite-sized, five- to ten-minute steps that make engaging with the physical world feel rewarding and low-friction, without asking you to shut down your imagination.",
      ],
    };
  }

  // steady tone (also default for empty / prefer-not-to-say)
  return {
    tone,
    showSupportSection: false,
    paragraphs: [
      "Your reflection indicates that your inner worlds and everyday life generally exist in a balanced relationship. The capacity to picture alternate paths, rich scenarios, and different identities is a hallmark of human creativity and problem-solving.",
      "Imaginative exploration gives us a safe sandbox to test what matters to us before making real-world commitments. When held in balance with sleep, work, and community, your daydreaming can be a supportive wellspring of energy and insight.",
      "The next sections will help you translate those imagined values into concrete experiments you can practice in your daily schedule.",
    ],
  };
}
